import { useEffect, useMemo, useState, useContext, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import GridLayout, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { SidebarContext } from "../../../shared/context/SidebarContext";
import { toast } from "react-toastify";
import { Pencil, Save, Plus, Copy, X, RefreshCw } from "lucide-react";
import { fetchCatalogo, fetchDashboard, criarDashboard, salvarDashboard, limparCacheQuery } from "../services/biApi";
import FiltrosGlobais, { periodoDoPreset } from "../components/bi/FiltrosGlobais";
import Widget from "../components/bi/Widget";
import WidgetEditor from "../components/bi/WidgetEditor";

const Grid = WidthProvider(GridLayout);
const ROW = 56; // px por unidade de altura do grid (12 colunas)

// Visualiza (modelo ou salvo) e edita (só salvo): filtros globais + grid de widgets.

export default function AdsDashboard() {
    const { isOpen } = useContext(SidebarContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const [dash, setDash] = useState(null);
    const [catalogo, setCatalogo] = useState(null);
    const [filtros, setFiltros] = useState(null);
    const [editando, setEditando] = useState(false);
    const [widgets, setWidgets] = useState([]);
    const [editor, setEditor] = useState(null); // widget em edição ({} = novo)
    const [sujo, setSujo] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [nome, setNome] = useState("");

    const carregar = useCallback(async () => {
        try {
            const [d, cat] = await Promise.all([fetchDashboard(id), catalogo || fetchCatalogo()]);
            setDash(d); setCatalogo(cat);
            setWidgets((d.layout?.widgets || []).map(w => ({ ...w })));
            setNome(d.nome);
            const fp = d.layout?.filtros_padrao || {};
            const preset = fp.preset || "7d";
            setFiltros(f => f || { preset, ...periodoDoPreset(preset), compare: fp.compare !== false, produto_id: fp.produto_id || null, conta_id: fp.conta_id || null, campanha_id: null });
            if (sp.get("editar") === "1" && !d.modelo) setEditando(true);
        } catch (e) {
            toast.error(e.response?.status === 404 ? "Dashboard não encontrado" : "Erro ao carregar dashboard");
            navigate("/ads/dashboards");
        }
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { carregar(); }, [carregar]);

    const layout = useMemo(() => widgets.map(w => ({ i: w.id, x: w.grid?.x ?? 0, y: w.grid?.y ?? 0, w: w.grid?.w ?? 4, h: w.grid?.h ?? 3, minW: 2, minH: 2 })), [widgets]);

    function onLayoutChange(l) {
        if (!editando) return;
        setWidgets(ws => ws.map(w => { const g = l.find(x => x.i === w.id); return g ? { ...w, grid: { x: g.x, y: g.y, w: g.w, h: g.h } } : w; }));
        setSujo(true);
    }
    function aplicarWidget(w) {
        setWidgets(ws => {
            if (ws.some(x => x.id === w.id)) return ws.map(x => x.id === w.id ? w : x);
            const maxY = ws.reduce((m, x) => Math.max(m, (x.grid?.y || 0) + (x.grid?.h || 0)), 0);
            const tipoH = { kpi: 2, texto: 2, funnel: 6, table: 6, creatives: 7 }[w.tipo] || 4;
            const tipoW = { kpi: 2, texto: 4, creatives: 12 }[w.tipo] || 6;
            return [...ws, { ...w, id: w.id || `w${Date.now().toString(36)}`, grid: { x: 0, y: maxY, w: tipoW, h: tipoH } }];
        });
        setEditor(null); setSujo(true);
    }
    function removerWidget(w) { setWidgets(ws => ws.filter(x => x.id !== w.id)); setSujo(true); }

    async function salvar() {
        setSalvando(true);
        try {
            const layoutNovo = { ...(dash.layout || {}), widgets, filtros_padrao: { ...(dash.layout?.filtros_padrao || {}), preset: filtros.preset || undefined, compare: !!filtros.compare, produto_id: filtros.produto_id || undefined, conta_id: filtros.conta_id || undefined } };
            const d = await salvarDashboard(dash.id, { nome, layout: layoutNovo });
            setDash(d); setSujo(false); setEditando(false);
            toast.success("Dashboard salvo");
        } catch (e) { toast.error(e.response?.data?.message || "Erro ao salvar"); }
        setSalvando(false);
    }
    async function duplicarEEditar() {
        try {
            const d = await criarDashboard({ de: dash.modelo ? dash.id : dash.id });
            navigate(`/ads/dashboards/${d.id}?editar=1`);
        } catch { toast.error("Erro ao duplicar"); }
    }
    function atualizar() { limparCacheQuery(); setFiltros(f => ({ ...f })); }

    if (!dash || !filtros || !catalogo) return <div className="min-h-screen flex"><div className={`${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full flex justify-center py-20`}><div className="w-8 h-8 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" /></div></div>;

    return (
        <div className="min-h-screen text-ink-1 flex relative z-0">
            <div className={`transition-all duration-300 ease-out-expo ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full max-w-full mb-16 sm:mb-0`}>
                <div className="px-5 sm:px-8 pt-6 pb-12 max-w-[1700px] mx-auto space-y-4">
                    <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="min-w-0">
                            <Link to="/ads/dashboards" className="text-xs text-ink-4 hover:text-ink-2">← Dashboards</Link>
                            {editando ? (
                                <input value={nome} onChange={e => { setNome(e.target.value); setSujo(true); }} className="input-field text-2xl font-display font-bold mt-1 w-full max-w-xl" />
                            ) : (
                                <h1 className="display-2 truncate">{dash.nome}{dash.modelo && <span className="chip-brand ml-3 align-middle">modelo</span>}</h1>
                            )}
                            {dash.descricao && !editando && <p className="text-ink-3 text-sm mt-0.5">{dash.descricao}</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button onClick={atualizar} className="btn-ghost text-xs flex items-center gap-1" title="Buscar dados de novo"><RefreshCw className="h-3.5 w-3.5" /> Atualizar</button>
                            {dash.modelo && <button onClick={duplicarEEditar} className="btn-primary text-xs flex items-center gap-1"><Copy className="h-3.5 w-3.5" /> Duplicar e editar</button>}
                            {!dash.modelo && !editando && <button onClick={() => setEditando(true)} className="btn-primary text-xs flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Editar</button>}
                            {editando && (
                                <>
                                    <button onClick={() => setEditor({})} className="btn-ghost text-xs flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Widget</button>
                                    <button onClick={salvar} disabled={salvando} className="btn-primary text-xs flex items-center gap-1"><Save className="h-3.5 w-3.5" /> {salvando ? "Salvando…" : "Salvar"}</button>
                                    <button onClick={() => { if (!sujo || window.confirm("Descartar alterações?")) { setEditando(false); setSujo(false); carregar(); } }} className="btn-ghost text-xs flex items-center gap-1"><X className="h-3.5 w-3.5" /></button>
                                </>
                            )}
                        </div>
                    </header>

                    <FiltrosGlobais filtros={filtros} onChange={setFiltros} opcoes={catalogo.opcoes} />

                    {editando && <p className="text-[11px] text-ink-4">Modo edição: arraste pela alça, redimensione pelo canto, edite ou remova pelo cabeçalho de cada widget. Os filtros atuais viram o padrão ao salvar.</p>}

                    {widgets.length === 0 && (
                        <div className="surface-card p-10 text-center text-sm text-ink-4">
                            Dashboard vazio. {editando ? <button onClick={() => setEditor({})} className="text-brand-300 underline">Adicione o primeiro widget</button> : "Edite pra adicionar widgets."}
                        </div>
                    )}

                    <Grid className="layout" layout={layout} cols={12} rowHeight={ROW} margin={[12, 12]} containerPadding={[0, 0]}
                        isDraggable={editando} isResizable={editando} draggableHandle=".drag-handle" onLayoutChange={onLayoutChange} compactType="vertical" useCSSTransforms>
                        {widgets.map(w => (
                            <div key={w.id}>
                                <Widget widget={w} filtrosGlobais={filtros} catalogo={catalogo} editando={editando} onEditar={setEditor} onRemover={removerWidget} />
                            </div>
                        ))}
                    </Grid>
                </div>
            </div>

            {editor && <WidgetEditor widget={editor} catalogo={catalogo} onSalvar={aplicarWidget} onFechar={() => setEditor(null)} />}
        </div>
    );
}
